import { safeLocalStorage } from '@apps-common/utils';
import { AuthenticationServiceTokenStrategy } from '@integration-frontends/common/auth/core/infrastructure';
import {
  AUTHENTICATE_SERVICE_TOKEN,
  IAuthenticateService,
} from '@integration-frontends/common/auth/core/model';
import { AuthUiModule } from '@integration-frontends/common/auth/ui';
import {
  BRANDFOLDER_API_TOKEN,
  BrandfolderApi,
} from '@integration-frontends/common/brandfolder-api';
import {
  ATTACHMENT_PLACEMENT_DETAILS_COMPONENT_KEY,
  BF_STANDARD_CHECKBOX_COMPONENT,
  BF_STANDARD_RADIO_COMPONENT,
  MULTI_SELECT_COMPONENT_KEY,
  SELECT_COMPONENT_KEY,
  TOGGLE_SWITCH_COMPONENT_KEY,
} from '@integration-frontends/common/ui';
import { SPINNER_COMPONENT_KEY } from '@integration-frontends/common/ui/loader/spinner';
import { BFStandardCheckboxSmartsheet } from '@integration-frontends/common/ui/standard-components/bf-standard-checkbox/bf-standard-checkbox-smartsheet';
import { BFStandardRadioSmartsheet } from '@integration-frontends/common/ui/standard-components/bf-standard-radio/bf-standard-radio-smartsheet';
import {
  ANALYTICS_SERVICE_TOKEN,
  AnalyticsServiceStrategy,
  BFHistory,
  BFStore,
  COMPONENT_RESOLVER_TOKEN,
  ComponentResolver,
  DI_CONTAINER,
  HISTORY_TOKEN,
  IAnalyticsService,
  ILogger,
  IObservabilityService,
  LOGGER_TOKEN,
  OBSERVABILITY_SERVICE_TOKEN,
  ObservabilityServiceDatadogStrategy,
  STORE_TOKEN,
} from '@integration-frontends/core';
import { BFStorage, STORAGE_TOKEN } from '@integration-frontends/core/storage';
import { ObservabilityUiModule } from '@integration-frontends/core/ui';
import {
  ISelectAttachmentOptions,
  IUploadAssetsOptions,
  SELECT_ATTACHMENT_OPTIONS_TOKEN,
  SelectActionType,
  SelectionType,
  TagsOrigin,
  UPLOAD_ASSETS_OPTIONS_TOKEN,
} from '@integration-frontends/integration/core/application';
import {
  ALL_IMAGE_TYPES,
  ASSET_REPO_TOKEN,
  ATTACHMENT_REPO_TOKEN,
  ATTACHMENT_TRANSFORM_SERVICE_TOKEN,
  BRANDFOLDER_REPO_TOKEN,
  BRANDFOLDER_WEBSITE_SERVICE_TOKEN,
  CDN_SERVICE_TOKEN,
  COLLECTION_REPO_TOKEN,
  CONTAINER_REPO_TOKEN,
  DOWNLOAD_SERVICE_TOKEN,
  IAssetRepo,
  IAttachmentRepo,
  IAttachmentTransformService,
  IBrandfolderRepo,
  IBrandfolderWebsiteService,
  ICdnService,
  ICollectionRepo,
  IContainerRepo,
  IDownloadService,
  ILabelRepo,
  IMediaTypeSupportService,
  IOrganizationRepo,
  ISearchFilterRepo,
  ISectionRepo,
  ISharingService,
  IUserSettingsRepo,
  LABEL_REPO_TOKEN,
  MEDIA_TYPE_SUPPORT_SERVICE_TOKEN,
  ORGANIZATION_REPO_TOKEN,
  ResourceBaseSortableProperty,
  SEARCH_FILTER_REPO_TOKEN,
  SECTION_REPO_TOKEN,
  SHARING_SERVICE_TOKEN,
  SortDirection,
  USER_SETTINGS_REPO_TOKEN,
} from '@integration-frontends/integration/core/model';
import {
  AssetRepo,
  AttachmentSelectorFeaturesService,
  AttachmentTransformService,
  BrandfolderRepo,
  BrandfolderWebsiteService,
  CdnService,
  CollectionRepo,
  ContainerRepo,
  DownloadService,
  GET_API_KEY_TOKEN,
  IGetAuthToken,
  LabelRepo,
  OrganizationRepo,
  SearchFilterRepo,
  SectionRepo,
  SharingService,
  UserSettingsRepo,
} from '@integration-frontends/integration/infrastructure/isomorphic';
import {
  ATTACHMENT_ACTION_PROVIDER_TOKEN,
  ATTACHMENT_SELECTOR_FEATURES_TOKEN,
  AttachmentSelectorFeature,
  IAttachmentActionProvider,
  IAttachmentSelectorFeaturesService,
  IntegrationUiModule,
} from '@integration-frontends/integration/ui';
import { ATTACHMENT_ACTIONS_CONTAINER_COMPONENT_KEY } from '@integration-frontends/integration/ui/attachment-selector/common/attachment-actions/attachment-actions-container';
import { ADVANCED_FILTERS_COMPONENT_KEY } from '@integration-frontends/integration/ui/attachment-selector/components/advanced-filters/advanced-filters-component';
import { ASSET_DETAILS_COMPONENT_KEY } from '@integration-frontends/integration/ui/attachment-selector/components/asset-details-page/asset-details/asset-details-component';
import { FILTERS_DISPLAY_COMPONENT_KEY } from '@integration-frontends/integration/ui/attachment-selector/components/show-page';
import { SMAR_ATTACHMENT_SELECTOR_COMPONENTS } from '@integration-frontends/integration/ui/attachment-selector/smar-attachment-selector-components';
import { ICON_VIEW_ONLY_COMPONENT_KEY } from '@integration-frontends/integration/ui/common/icons';
import { Container } from 'inversify';
import {
  IDENTITY_STORE_TOKEN,
  IdentityStoreLocalStorageStrategy,
  IIdentityStore,
} from './app/common';
import { SmarMultiSelect } from './app/common/ui/src/lib/forms/smar-multi-select';
import { SmarSelect } from './app/common/ui/src/lib/forms/smar-select';
import { SmarToggleSwitch } from './app/common/ui/src/lib/forms/smar-toggle-switch';
import { SmarFiltersDisplay } from './app/common/ui/src/lib/smar-filter-display/smar-filters-display';
import { SmarSpinner } from './app/common/ui/src/lib/smar-spinner';
import { createGetApiKeyCustomStrategy } from './app/get-api-key';
import { AttachmentActionsProviderSmartsheetStrategy } from './app/infrastructure/attachment-actions-provider-smartsheet-strategy';
import { AttachmentRepoPanelUiStrategy } from './app/infrastructure/AttachmentRepoPanelUiStrategy';
import { createFailureCallback } from './app/infrastructure/bf-api-failure-handler';
import { MediaTypeSupportServiceSmartsheetStrategy } from './app/infrastructure/media-type-support-service-smartsheet-strategy';
import { createStore } from './app/store';
import { SmarAttachmentActionsContainer } from './app/ui/attachment-selector/attachment-action-containers/smar-attachment-actions-container';
import { SmarIconViewOnly } from './app/ui/attachment-selector/common/icons/smar-icon-view-only';
import { SmarAdvancedFilters } from './app/ui/attachment-selector/smar-advanced-filters/smar-advanced-filters';
import { SmarAssetDetailsComponent } from './app/ui/attachment-selector/smar-asset-details-page/smar-asset-details-component';
import { SmarAttachmentPlacementDetails } from './app/ui/attachment-selector/smar-attachment-placement-details/smar-attachment-placement-details';
import { BF_BASE_URL, BF_STATIC_ASSETS_BASE_URL, BF_WEBSITE_BASE_URL } from './config';
import './i18n';

// TODO: use BFModule. ~PP

/**
 * App
 */
const appContainer = new Container();
appContainer.bind<ILogger>(LOGGER_TOKEN).toConstantValue(console);
appContainer.bind<IIdentityStore>(IDENTITY_STORE_TOKEN).to(IdentityStoreLocalStorageStrategy);
appContainer.bind<BFStorage>(STORAGE_TOKEN).toConstantValue(safeLocalStorage);
DI_CONTAINER.merge(appContainer);

/**
 * Store
 */
const storeContainer = new Container();
const { store, history } = createStore();
storeContainer.bind<BFStore>(STORE_TOKEN).toConstantValue(store);
storeContainer.bind<BFHistory>(HISTORY_TOKEN).toConstantValue(history);
DI_CONTAINER.merge(storeContainer);

/**
 * Auth
 */
const authContainer = new Container();
authContainer
  .bind<IAuthenticateService>(AUTHENTICATE_SERVICE_TOKEN)
  .to(AuthenticationServiceTokenStrategy);

AuthUiModule.initialize(authContainer);

/**
 * Integration
 */
const integrationContainer = new Container();

integrationContainer
  .bind<BrandfolderApi>(BRANDFOLDER_API_TOKEN)
  .toConstantValue(
    new BrandfolderApi(BF_BASE_URL, DI_CONTAINER.get(LOGGER_TOKEN), createFailureCallback(store)),
  );

integrationContainer
  .bind<IGetAuthToken>(GET_API_KEY_TOKEN)
  .toConstantValue(createGetApiKeyCustomStrategy(store));
integrationContainer.bind<IAttachmentRepo>(ATTACHMENT_REPO_TOKEN).to(AttachmentRepoPanelUiStrategy);
integrationContainer
  .bind<IAttachmentTransformService>(ATTACHMENT_TRANSFORM_SERVICE_TOKEN)
  .to(AttachmentTransformService);
integrationContainer.bind<IAssetRepo>(ASSET_REPO_TOKEN).to(AssetRepo);
integrationContainer.bind<IBrandfolderRepo>(BRANDFOLDER_REPO_TOKEN).to(BrandfolderRepo);
integrationContainer.bind<ICollectionRepo>(COLLECTION_REPO_TOKEN).to(CollectionRepo);
integrationContainer.bind<ISearchFilterRepo>(SEARCH_FILTER_REPO_TOKEN).to(SearchFilterRepo);
integrationContainer.bind<ISectionRepo>(SECTION_REPO_TOKEN).to(SectionRepo);
integrationContainer.bind<IOrganizationRepo>(ORGANIZATION_REPO_TOKEN).to(OrganizationRepo);
integrationContainer.bind<ILabelRepo>(LABEL_REPO_TOKEN).to(LabelRepo);
integrationContainer.bind<IContainerRepo>(CONTAINER_REPO_TOKEN).to(ContainerRepo);
integrationContainer.bind<IUserSettingsRepo>(USER_SETTINGS_REPO_TOKEN).to(UserSettingsRepo);
integrationContainer
  .bind<IMediaTypeSupportService>(MEDIA_TYPE_SUPPORT_SERVICE_TOKEN)
  .toConstantValue(new MediaTypeSupportServiceSmartsheetStrategy(ALL_IMAGE_TYPES));
integrationContainer.bind<IDownloadService>(DOWNLOAD_SERVICE_TOKEN).to(DownloadService);
integrationContainer.bind<ISharingService>(SHARING_SERVICE_TOKEN).to(SharingService);
integrationContainer
  .bind<IBrandfolderWebsiteService>(BRANDFOLDER_WEBSITE_SERVICE_TOKEN)
  .toConstantValue(new BrandfolderWebsiteService(BF_WEBSITE_BASE_URL, BF_STATIC_ASSETS_BASE_URL));
integrationContainer
  .bind<IAttachmentSelectorFeaturesService>(ATTACHMENT_SELECTOR_FEATURES_TOKEN)
  .toConstantValue(new AttachmentSelectorFeaturesService([AttachmentSelectorFeature.Responsive]));
integrationContainer
  .bind<ISelectAttachmentOptions>(SELECT_ATTACHMENT_OPTIONS_TOKEN)
  .toConstantValue({
    actionType: SelectActionType.Select,
    selectionType: SelectionType.Multi,
    customSortOptions: {
      field: ResourceBaseSortableProperty.CreatedAt,
      direction: SortDirection.Desc,
    },
    showAttachmentsToggleDisabled: true,
    showAToZSorting: false,
  });
integrationContainer.bind<IUploadAssetsOptions>(UPLOAD_ASSETS_OPTIONS_TOKEN).toConstantValue({
  showAddMore: false,
  showTags: true,
  showCustomFields: true,
  multipleAssetUpload: false,
  tagsOrigin: TagsOrigin.external,
});
integrationContainer
  .bind<IAttachmentActionProvider>(ATTACHMENT_ACTION_PROVIDER_TOKEN)
  .toConstantValue(new AttachmentActionsProviderSmartsheetStrategy());

export const smarComponentResolver = new ComponentResolver({
  [ADVANCED_FILTERS_COMPONENT_KEY]: SmarAdvancedFilters,
  [ATTACHMENT_ACTIONS_CONTAINER_COMPONENT_KEY]: SmarAttachmentActionsContainer,
  [BF_STANDARD_CHECKBOX_COMPONENT]: BFStandardCheckboxSmartsheet,
  [FILTERS_DISPLAY_COMPONENT_KEY]: SmarFiltersDisplay,
  [ICON_VIEW_ONLY_COMPONENT_KEY]: SmarIconViewOnly,
  [BF_STANDARD_RADIO_COMPONENT]: BFStandardRadioSmartsheet,
  [SELECT_COMPONENT_KEY]: SmarSelect,
  [MULTI_SELECT_COMPONENT_KEY]: SmarMultiSelect,
  [TOGGLE_SWITCH_COMPONENT_KEY]: SmarToggleSwitch,
  [ASSET_DETAILS_COMPONENT_KEY]: SmarAssetDetailsComponent,
  [SPINNER_COMPONENT_KEY]: SmarSpinner,
  [ATTACHMENT_PLACEMENT_DETAILS_COMPONENT_KEY]: SmarAttachmentPlacementDetails,
  ...SMAR_ATTACHMENT_SELECTOR_COMPONENTS,
});

integrationContainer
  .bind<ComponentResolver>(COMPONENT_RESOLVER_TOKEN)
  .toConstantValue(smarComponentResolver);
integrationContainer.bind<ICdnService>(CDN_SERVICE_TOKEN).to(CdnService);

integrationContainer
  .bind<IAnalyticsService>(ANALYTICS_SERVICE_TOKEN)
  .to(AnalyticsServiceStrategy);

IntegrationUiModule.initialize(integrationContainer);

/** Observability
 */
const observabilityContainer = new Container();
observabilityContainer
  .bind<IObservabilityService>(OBSERVABILITY_SERVICE_TOKEN)
  .to(ObservabilityServiceDatadogStrategy);

ObservabilityUiModule.initialize(observabilityContainer);
